import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../BottomNav';
import axiosInstance from '../../api/axiosInstance';
import { useSocket } from "../../context/SocketContext";
import { useAuth } from "../../context/AuthContext";
import { getUsername } from "../../utils/userUtils";
import PullToRefresh from '../common/PullToRefresh';
import { CircularProgress } from '@mui/material';
import { getTimeAgo } from '../../utils/timeUtils';
import { SquarePen, Search } from 'lucide-react';

const SeeChats = () => {
  const { socket } = useSocket();
  const { userId } = useAuth();

  const [chats, setChats] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const PAGE_SIZE = 30;
  const chatsRef = useRef([]);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  const handleRefresh = async () => {
    try {
      const response = await axiosInstance.get("/chat/get_conversations", { params: { limit: PAGE_SIZE, offset: 0 } });
      setChats(response.data);
      setOffset(response.data.length);
      setHasMore(response.data.length === PAGE_SIZE);
    } catch (err) {
      console.error('Failed to refresh chats:', err);
    }
  };
  
  useEffect(() => {
    if (socket && userId) {
      socket.emit('join rooms', userId);
    }
  }, [socket, userId]);

  useEffect(() => {
    const fetchChats = async () => {
      try {
        const response = await axiosInstance.get("/chat/get_conversations", { params: { limit: PAGE_SIZE, offset: 0 } });
        setChats(response.data);
        setOffset(response.data.length);
        setHasMore(response.data.length === PAGE_SIZE);
      } catch (err) {
        console.error('Failed to load chats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchChats();
  }, []);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const response = await axiosInstance.get("/chat/get_conversations", { params: { limit: PAGE_SIZE, offset } });
      const newItems = response.data || [];
      if (newItems.length > 0) {
        // merge without duplicates
        setChats((prev) => {
          const map = new Map(prev.map((c) => [c.chat_room_id, c]));
          for (const item of newItems) {
            map.set(item.chat_room_id, { ...(map.get(item.chat_room_id) || {}), ...item });
          }
          // Keep ordering: newest first as returned by backend across pages
          const merged = Array.from(map.values());
          // Sort by last_message_timestamp desc (fallback empty last)
          merged.sort((a, b) => {
            const ta = a.last_message_timestamp ? new Date(a.last_message_timestamp).getTime() : 0;
            const tb = b.last_message_timestamp ? new Date(b.last_message_timestamp).getTime() : 0;
            return tb - ta;
          });
          return merged;
        });
      }
      setOffset((prev) => prev + newItems.length);
      setHasMore(newItems.length === PAGE_SIZE);
    } catch (err) {
      console.error('Failed to load more chats:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!socket) return;

    const onMessage = (newMsg) => {
      getUsername(newMsg.sender)
        .then((username) => {
          setChats((prevChats) => {
            const idx = prevChats.findIndex(c => c.chat_room_id === newMsg.chat_id);
            let updated;
            if (idx > -1) {
              const updatedChat = {
                ...prevChats[idx],
                last_message: newMsg.content,
                last_message_sender: username,
                type: newMsg.type,
                last_message_timestamp: newMsg.timestamp,
                last_message_unseen: true
              };

              updated = [updatedChat, ...prevChats.filter((_, i) => i !== idx)];
            }
            else {
              updated = [{
                chat_room_id: newMsg.chat_id,
                chat_name: newMsg.chat_name || 'New Chat',
                last_message: newMsg.content,
                last_message_sender: username,
                type: newMsg.type,
                last_message_timestamp: newMsg.timestamp,
                last_message_unseen: true
              }, ...prevChats];
            }
            return updated;
          });
      })
    };

    socket.on('chat message', onMessage);
    return () => socket.off('chat message', onMessage);
  }, [socket]);

  const filteredChats = chats.filter(chat =>
    chat.chat_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Progressive search: if not found locally and more pages exist, fetch until found or exhaust pages
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return; // nothing to do
      if (filteredChats.length > 0) return; // already found in local
      // attempt to fetch more pages up to a safe cap (e.g. 10 pages)
      let attempts = 0;
      while (!cancelled && hasMore && attempts < 10) {
        await loadMore();
        attempts += 1;
        // check again using latest chats from ref
        const found = chatsRef.current.some(c => c.chat_name?.toLowerCase().includes(q));
        if (found) break;
      }
    };
    // Debounce a bit to avoid hammering while typing
    const t = setTimeout(run, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // We intentionally depend on searchQuery, hasMore; not on filteredChats/loadMore to avoid immediate re-runs across loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, hasMore]);

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      className="bg-white dark:bg-darkBackground min-h-screen text-black dark:text-white transition-colors"
      contentClassName="min-h-screen max-w-xl mx-auto p-4 pt-12 pb-16"
    >
        <div className="flex justify-between items-center mx-1 mb-4">
          <h1 className="text-3xl font-bold">Chat</h1>
          <button
            className="p-2 rounded-full transition-colors"
            onClick={() => navigate('/chat/new')}
          >
            <SquarePen className="w-5 h-5 text-black dark:text-white" />
          </button>
        </div>

        <div className="relative mb-5">
          <Search className="absolute left-3 top-3 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-neutral-500" />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-neutral-800 rounded-full focus:outline-none bg-white dark:bg-neutral-800 placeholder-gray-400 dark:placeholder-neutral-500 text-black dark:text-white"
          />
        </div>

        {loading ? (
          <div className='loading-container-comments'>
            <CircularProgress size={40} sx={{ color: '#d1d5db' }} />
          </div>
        ) : filteredChats.length === 0 ? (
          <p className="pt-5 text-lg text-center text-gray-500 dark:text-gray-300">No conversations yet</p>
        ) : (
          <div className="space-y-1 divide-y divide-gray-200 dark:divide-neutral-800">
            {filteredChats.map((chat) => (
              <div
                key={chat.chat_room_id}
                onClick={() => 
                  navigate(`/chat/${chat.chat_room_id}`)
                }
                className="flex items-start gap-4 px-4 py-3 cursor-pointer transition-colors"
              >
                <img
                  src={chat.profile_url || (chat.is_group ? '/assets/images/default_group_picture.png' : '/assets/images/default_profile.png')}
                  alt={chat.is_group ? 'Group' : 'Profile'}
                  className="w-12 h-12 rounded-full object-cover"
                  loading="lazy"
                />

                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center">
                    <h2 className={`truncate ${chat.last_message_unseen ? 'font-bold' : 'font-medium'}`}>
                      {chat.is_group ? chat.chat_name : chat.chat_name || 'Unknown'}
                    </h2>
                    <span className={`text-xs ${chat.last_message_unseen ? 'text-black dark:text-white' : 'text-gray-400'}`}>
                      {chat.last_message_timestamp ? getTimeAgo(chat.last_message_timestamp) : ''}
                    </span>
                  </div>
                  <p className={`text-sm ${chat.last_message_unseen ? 'text-black dark:text-white font-medium' : 'text-gray-500 dark:text-gray-400'} min-w-0`}>
                    <span className="block truncate align-bottom">
                      {chat.last_message_sender
                        ? chat.last_message_sender
                        : ''}
                      {!chat.type
                        ? chat.last_message ? `: ${chat.last_message}` : 'No messages yet'
                        : chat.type === 'audio'
                          ? ' sent a voice message'
                          : chat.type === 'photo'
                            ? ' sent a photo'
                            : chat.type === 'video'
                              ? ' sent a video'
                              : chat.type === 'file'
                                ? ' sent a file'
                                : chat.type === 'audio call'
                                  ? ` audio called ${chat.last_message_sender == 'You' ? 'them' : 'you'}`
                                  : chat.type === 'video call'
                                    ? ` video called ${chat.last_message_sender == 'You' ? 'them' : 'you'}`
                                    : chat.type === 'deleted'
                                      ? ' unsent a message'
                                      : ''
                      }
                    </span>
                  </p>
                </div>
              </div>
            ))}
            {hasMore && (
              <div className="py-4 flex justify-center">
                <button
                  disabled={loadingMore}
                  onClick={loadMore}
                  className="px-4 py-2 text-sm rounded-full border border-gray-300 dark:border-neutral-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-800 disabled:opacity-50"
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        )}
        <BottomNav />
    </PullToRefresh>
  );
};

export default SeeChats;